import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/gambio-zu-shopify";
const TITLE = "Gambio zu Shopify umziehen: Daten, Adressen, Kosten";
const DESCRIPTION =
  "Von Gambio zu Shopify wechseln: Produktexport, Kategoriestruktur, Rechtstexte, Weiterleitungen der alten Adressen und realistische Kosten des Umzugs.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Beim Wechsel von Gambio zu Shopify werden Produkte, Kategorien und Kunden exportiert, die Kategoriestruktur in Shopify-Collections neu aufgebaut und alle alten Adressen per 301 weitergeleitet. Rechtstexte und Versandregeln werden neu gesetzt, nicht kopiert. Kleinere Shops sind in drei bis sechs Wochen umgezogen.";

const faqs = [
  {
    q: "Lassen sich Gambio-Daten nach Shopify übernehmen?",
    a: "Produkte, Kategorien und Kunden lassen sich über CSV-Exporte übertragen. Rechnen Sie mit Nacharbeit bei Varianten, Bildzuordnung und Artikelattributen, weil Gambio diese anders ablegt als Shopify.",
  },
  {
    q: "Was passiert mit meinen Rechtstexten?",
    a: "Impressum, Widerruf, Versand und Datenschutz werden im neuen Shop neu eingerichtet und sollten von Ihrem Rechtsdienstleister für Shopify aktualisiert werden. Ein reines Kopieren der alten Texte ist riskant, weil Prozesse und Zahlungsarten sich ändern.",
  },
  {
    q: "Bleiben meine Google-Platzierungen erhalten?",
    a: "Mit vollständigen 301-Weiterleitungen für Artikel-, Kategorie- und Inhaltsseiten in aller Regel ja. Achten Sie besonders auf alte Adressmuster mit Parametern, die noch Rankings halten.",
  },
  {
    q: "Was kostet der Umzug von Gambio zu Shopify?",
    a: "Kleinere Shops liegen meist bei 4.000 bis 8.000 € netto inklusive Theme und Weiterleitungen, größere Sortimente mit Schnittstellen darüber. Entwicklung berechne ich mit 110 € netto pro Stunde.",
  },
  {
    q: "Wie lange dauert der Wechsel?",
    a: "Drei bis sechs Wochen für einen überschaubaren Katalog, inklusive Datenprüfung, Zahlungsarten, Versandregeln und Testbestellungen.",
  },
];

export const Route = createFileRoute("/ratgeber/gambio-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="Gambio zu Shopify: sauberer Schnitt statt Nachbau"
      intro="Gambio-Shops sind meist über Jahre gewachsen. Beim Umzug lohnt es sich nicht, jedes Detail nachzubauen, sondern Katalog, Kategorien und Kaufabschluss an dem auszurichten, wonach Kunden heute suchen."
      source="ratgeber-gambio-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Was den Umzug einfach macht",
          p: [
            "Die Datenmengen sind bei Gambio-Shops meist überschaubar, und die Sortimente sind klar strukturiert. Der Export von Produkten, Kategorien und Kunden ist damit selten der Engpass.",
          ],
        },
        {
          h: "Wo Nacharbeit anfällt",
          p: [],
          list: [
            "Varianten und Artikelattribute auf Shopify-Varianten und Metafelder abbilden",
            "Bildzuordnung nach dem Import stichprobenartig prüfen",
            "Versand- und Steuerregeln neu aufsetzen",
            "Rechtstexte für Shopify neu einrichten",
            "Zahlungsarten wählen und Testbestellungen fahren",
          ],
        },
        {
          h: "Adressen und Sichtbarkeit",
          p: [
            "Ziehen Sie die Adressliste aus dem Shop und aus der Search Console und geben Sie jeder Adresse ein Ziel. Gerade ältere Gambio-Installationen haben Adressmuster mit Parametern, die noch Rankings tragen.",
          ],
        },
        {
          h: "Kosten und Zeitrahmen",
          p: [
            "Kleinere Shops liegen meist bei 4.000 bis 8.000 € netto und drei bis sechs Wochen. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/ratgeber/jtl-zu-shopify", label: "JTL-Shop zu Shopify" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shopify Shop erstellen lassen" },
        { to: "/kontakt", label: "Umzug anfragen" },
      ]}
    />
  );
}
